import { useCallback, useEffect, useState } from "react";

import { type ClaimHistoryItem, type ClaimHistorySummary, invoicingEndpoints } from "../../../api";
import getErrorMessage from "../../../utils/getErrorMessage";
import handleError from "../../utils/handleError";

interface UseClaimHistoryProps {
  autoLoad?: boolean;
}

/**
 * Hook to fetch merged claim history (old payouts + new invoices).
 *
 * Returns data in a unified format that PayoutsTableCard can display.
 * Each item has a `_source` field ('payout' or 'invoice') to distinguish.
 */
const useClaimHistory = ({ autoLoad = true }: UseClaimHistoryProps = {}) => {
  const [claims, setClaims] = useState<ClaimHistoryItem[]>([]);
  const [summary, setSummary] = useState<ClaimHistorySummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await invoicingEndpoints.getClaimHistory();
      setClaims(data.claims || []);
      setSummary(data.summary);
    } catch (err: unknown) {
      handleError(getErrorMessage(err, "Failed to load claim history"));
      setClaims([]);
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoLoad) {
      refresh();
    }
  }, [autoLoad, refresh]);

  return {
    claims,
    summary,
    isLoading,
    refresh,
  };
};

export default useClaimHistory;
